<!--
  - Copyright (c) 2019 brightlamp
  -->

<template>
    <div>
    <ValidationObserver ref="observer" v-slot="{ invalid }">
        <v-form>
            <v-progress-linear v-if="waiting" :indeterminate="true"/>
            <v-checkbox
                    type="checkbox"
                    value="1"
                    label="Keep me logged in"
                    v-model="form.remember"
                    color="primary"
                    :disabled="waiting"/>
            <v-btn
                    class="mb-3 white"
                    block
                    large
                    @click="googleSignIn()"
                    :disabled="waiting">
                <v-row>
                    <v-avatar size="35">
                        <v-img :src="require('@/assets/images/google@3x.png')"/>
                    </v-avatar>
                </v-row>
                <v-row>
                    Google Login/Sign Up
                </v-row>
            </v-btn>
            <v-btn
                    class="mb-3 white"
                    block
                    large
                    @click="facebookSignIn()"
                    :disabled="waiting">
                <v-row>
                    <v-avatar size="35">
                        <v-img :src="require('@/assets/images/facebook@3x.png')"/>
                    </v-avatar>
                </v-row>
                <v-row>
                    Facebook Login/Sign Up
                </v-row>
            </v-btn>
            <v-btn
                    class="mb-7 white"
                    block
                    large
                    @click="appleSignIn()"
                    :disabled="waiting">
                <v-row>
                    <v-avatar size="35">
                        <v-img :src="require('@/assets/images/apple-white-logo-masked-circular.png')"/>
                    </v-avatar>
                </v-row>
                <v-row>
                    Apple Login/Sign Up
                </v-row>
            </v-btn>
            <ValidationProvider
                    mode="aggressive"
                    rules="requiredEmail|email"
                    :debounce="300"
                    v-slot="{ errors }">
                <v-text-field
                        type="email"
                        label="Email"
                        v-model="form.email"
                        :error-messages=errors[0]
                        solo
                        :disabled="waiting"/>
            </ValidationProvider>
            <ValidationProvider
                    mode="aggressive"
                    rules="requiredPassword|minPassword:6"
                    :debounce=300
                    v-slot="{ errors }">
                <v-text-field
                        type="password"
                        label="Password"
                        v-model="form.password"
                        :error-messages=errors[0]
                        solo
                        :disabled="waiting"/>
            </ValidationProvider>
            <v-btn
                    type="submit"
                    class="mb-3 primary"
                    block
                    large
                    @click="submit()"
                    :disabled="invalid || waiting">Email & Password Login</v-btn>
            <v-btn
                    class="mb-3 indigo white--text"
                    block
                    large
                    :to="{ name: 'SignUp' }"
                    :disabled="waiting">Email & Password Sign Up</v-btn>
            <v-btn
                    block
                    large
                    :to="{ name: 'PasswordRecovery' }"
                    :disabled="waiting">Password Recovery For Email</v-btn>
            <v-row class="mt-3" justify="space-around">
                <a href="https://reflexapp.io/terms-conditions/" target="_blank">Terms + Conditions</a>
                <a href="https://reflexapp.io/privacy-policy/" target="_blank">Privacy Policy</a>
            </v-row>
            <v-row>
                <p class="mt-4  text-caption font-italic">Creating an account on The Reflex Web Portal is not a subscription to Reflex or Reflex Pro and you will not be billed for accessing learning content.</p>
            </v-row>
        </v-form>
    </ValidationObserver>
    </div>
</template>

<script>
    import { mapState, mapMutations, mapActions } from 'vuex'
    import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
    import { email, required, min } from 'vee-validate/dist/rules';
    import { signInWithEmailAndPassword, signInWithPopup, signInWithRedirect, GoogleAuthProvider, FacebookAuthProvider, OAuthProvider, setPersistence, browserLocalPersistence, browserSessionPersistence } from '@/firebase/firebase';

    extend('requiredEmail',
    {
        ...required,
        message: 'A valid email address is required.'
    });
    extend('email',
    {
        ...email,
        message: 'A valid email address is required.'
    });
    extend('requiredPassword',
    {
        ...required,
        message: 'The password is required and must be at least 6 characters.'
    });
    extend('minPassword',
    {
        ...min,
        message: 'The password is required and must be at least 6 characters.'
    });

    export default
    {
        name: 'login',
        components:
        {
            ValidationProvider,
            ValidationObserver
        },
        data()
        {
            return {
                form:
                {
                    email: '',
                    password: '',
                    remember: null
                }
            }
        },
        computed:
        {
            ...mapState('common',
            {
                waiting: state => state.waiting
            }),
            firebasePersistence()
            {
                return this.form.remember ? browserLocalPersistence : browserSessionPersistence
            }
        },
        methods:
        {
            ...mapMutations('common', ['WAITING', 'SHOW_SNACKBAR_ERROR', 'HIDE_SNACKBAR']),
            ...mapActions('auth', ['LOGOUT_AUTH']),
            async submit()
            {
                try
                {
                    if (await this.$refs.observer.validate())
                    {
                        this.WAITING(true);
                        this.HIDE_SNACKBAR();
                        await setPersistence(this.$firebaseAuth, this.firebasePersistence);
                        await signInWithEmailAndPassword(this.$firebaseAuth, this.form.email, this.form.password);
                        if ('from' in this.$route.query)
                        {
                            try
                            {
                                await this.$router.replace(this.$route.query.from);
                            }
                            catch (error)
                            {
                            }
                        }
                        else
                        {
                            try
                            {
                                await this.$router.replace(
                                {
                                    name: 'Home'
                                })
                            }
                            catch (error)
                            {
                            }
                        }
                    }
                }
                catch (error)
                {
                    let errorMessage;
                    switch (error.code)
                    {
                        case 'auth/user-not-found':
                        case 'auth/wrong-password':
                            errorMessage = 'Email not on file or Password is incorrect.';
                            break
                        default:
                            errorMessage = 'System Error.';
                    }
                    this.WAITING(false);
                    this.form.password = '';
                    // You should call it on the next frame
                    window.requestAnimationFrame(() =>
                    {
                        this.$refs.observer.reset()
                    });
                    this.SHOW_SNACKBAR_ERROR(errorMessage)
                }
            },
            async googleSignIn()
            {
                const gap = new GoogleAuthProvider();
                // Allow the user to select a google account
                gap.setCustomParameters({prompt: 'select_account'});
                await this.commonSignIn(gap)
            },
            async facebookSignIn()
            {
                await this.commonSignIn(new FacebookAuthProvider())
            },
            async appleSignIn()
            {
                await this.commonSignIn(new OAuthProvider('apple.com'))
            },
            async commonSignIn(provider)
            {
                this.WAITING(true);
                this.HIDE_SNACKBAR();
                this.$firebaseAuth.useDeviceLanguage();
                await setPersistence(this.$firebaseAuth, this.firebasePersistence);
                try
                {
                    // await signInWithRedirect(this.$firebaseAuth, provider)
                    await signInWithPopup(this.$firebaseAuth, provider);
                    if ('from' in this.$route.query)
                    {
                        await this.$router.replace(this.$route.query.from);
                    }
                    else
                    {
                        await this.$router.replace({
                            name: 'Home'
                        })
                    }
                }
                catch (error)
                {
                    this.WAITING(false);
                    if (error.message.startsWith('Firebase: Error (auth/popup-blocked).'))
                    {
                        this.SHOW_SNACKBAR_ERROR('Popup blocker preventing login. Please just try again or turn off popup blocking for this website.')
                    }
                    else if (error.message !== 'Firebase: Error (auth/popup-closed-by-user).' &&
                        !error.message.startsWith('Redirected when going from'))
                    {
                        this.SHOW_SNACKBAR_ERROR(error.message)
                    }
                }
            }
        },
        async mounted()
        {
            await this.LOGOUT_AUTH();
        }
    }
</script>

<style scoped>
</style>
